import { useState } from 'react';
import { Sparkles, ArrowRight, Headphones, Heart, Zap, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import VoiceWave from '@/components/VoiceWave';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ConfidenceLandingPage() {
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [conflictMsg, setConflictMsg] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed) {
      toast.error('Please enter your email');
      return;
    }
    setConflictMsg('');
    setSubmitting(true);

    // Use XHR + hard redirect (same pattern as MagicAccessPage) so the
    // session cookie is fully in place before the next route mounts.
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${API_URL}/api/leads/email-access`);
    xhr.setRequestHeader('Content-Type', 'application/json');
    xhr.withCredentials = true;
    xhr.onload = function () {
      if (xhr.status >= 200 && xhr.status < 300) {
        window.location.href = '/confidence/library';
        return;
      }
      let msg = 'Something went wrong. Please try again.';
      try {
        const err = JSON.parse(xhr.responseText);
        if (err.detail) msg = err.detail;
      } catch {}
      if (xhr.status === 409) {
        // Email already registered — surface inline with login link
        setConflictMsg(msg);
      } else {
        toast.error(msg);
      }
      setSubmitting(false);
    };
    xhr.onerror = function () {
      toast.error('Network error. Please try again.');
      setSubmitting(false);
    };
    xhr.send(JSON.stringify({ email: trimmed, category: 'confidence' }));
  };

  return (
    <div className="min-h-screen bg-zinc-950 relative overflow-hidden" data-testid="confidence-landing-page">
      {/* Ambient gradient background */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-[300px] sm:w-[500px] h-[300px] sm:h-[500px] bg-amber-500/10 rounded-full blur-[120px]" />
        <div className="absolute bottom-0 right-0 w-[250px] sm:w-[400px] h-[250px] sm:h-[400px] bg-violet-500/10 rounded-full blur-[120px]" />
      </div>

      <main className="relative container mx-auto px-5 sm:px-6 py-12 sm:py-16 md:py-24 max-w-3xl">
        {/* Brand */}
        <div className="flex items-center gap-3 mb-8 sm:mb-12">
          <VoiceWave size="sm" speed={1.1} lineCount={4} />
          <div className="flex items-center gap-2 opacity-70">
            <Sparkles className="w-4 h-4 text-amber-400" />
            <span className="text-sm tracking-widest text-zinc-400 uppercase">Lifetunes</span>
          </div>
        </div>

        {/* Hero */}
        <div className="space-y-5 sm:space-y-6 mb-8 sm:mb-12">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-500/10 border border-amber-500/20 text-amber-300 text-xs font-medium tracking-wide">
            <Zap className="w-3 h-3" /> Confidence Series
          </div>
          <h1 className="text-3xl sm:text-5xl lg:text-6xl font-serif text-white leading-[1.1] tracking-tight">
            Build unshakable<br />
            <span className="text-amber-300 italic">self-belief</span> in 5 minutes a day.
          </h1>
          <p className="text-base sm:text-lg text-zinc-400 max-w-xl leading-relaxed">
            Just type your email and start listening. No password, no signup forms — instant access to our Confidence track library.
          </p>
        </div>

        {/* Email form */}
        <form onSubmit={handleSubmit} className="mb-10" data-testid="confidence-email-form">
          <div className="flex flex-col sm:flex-row gap-3 max-w-xl">
            <input
              type="email"
              required
              autoComplete="email"
              inputMode="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className="flex-1 bg-zinc-900/80 border border-white/10 rounded-xl px-5 py-4 text-white placeholder:text-zinc-600 focus:border-amber-500/50 focus:ring-2 focus:ring-amber-500/20 focus:outline-none text-base"
              data-testid="confidence-email-input"
              disabled={submitting}
            />
            <button
              type="submit"
              disabled={submitting}
              className="group inline-flex items-center justify-center gap-2 bg-amber-400 hover:bg-amber-300 text-zinc-950 font-semibold px-6 py-4 rounded-xl transition-all disabled:opacity-50 disabled:cursor-wait whitespace-nowrap"
              data-testid="confidence-submit-btn"
            >
              {submitting ? 'Starting…' : (
                <>
                  Start Listening
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                </>
              )}
            </button>
          </div>
          <p className="text-xs text-zinc-600 mt-3">
            We'll save your progress and send tips occasionally. No spam — promise.
          </p>

          {conflictMsg && (
            <div
              className="mt-4 max-w-xl flex items-start gap-3 p-3.5 rounded-xl bg-red-500/10 border border-red-500/30 text-red-200"
              data-testid="confidence-conflict-banner"
              role="alert"
            >
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-medium">{conflictMsg}</p>
                <div className="mt-2 flex flex-wrap gap-3 text-xs">
                  <a
                    href="/login"
                    className="text-amber-300 hover:text-amber-200 font-medium underline underline-offset-2"
                    data-testid="confidence-login-link"
                  >
                    Log in instead
                  </a>
                  <button
                    type="button"
                    onClick={() => { setConflictMsg(''); setEmail(''); }}
                    className="text-zinc-400 hover:text-zinc-200"
                    data-testid="confidence-try-different-btn"
                  >
                    Use a different email
                  </button>
                </div>
              </div>
            </div>
          )}
        </form>

        {/* Mini features */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 pt-6 sm:pt-8 border-t border-white/5">
          {[
            { icon: Headphones, title: 'Curated Tracks', desc: 'Affirmations & meditations crafted to rewire your inner narrative.' },
            { icon: Heart, title: 'Save Favorites', desc: 'Build your personal confidence playlist as you listen.' },
            { icon: Sparkles, title: 'No Pressure', desc: 'Start free — upgrade only when you\'re ready for the full library.' },
          ].map(({ icon: Icon, title, desc }, i) => (
            <div key={i} className="p-4 rounded-xl bg-zinc-900/40 border border-white/5">
              <Icon className="w-5 h-5 text-amber-400 mb-3" />
              <p className="text-sm text-zinc-200 font-medium mb-1">{title}</p>
              <p className="text-xs text-zinc-500 leading-relaxed">{desc}</p>
            </div>
          ))}
        </div>
      </main>
    </div>
  );
}
